/**
 * THE QUOTE: WHAT A PERSON TYPED, ARITHMETIC, AND NOTHING ELSE.
 *
 * This file computes no footage, reads no geometry and looks at no photograph.
 * That is the whole design, and it is the opposite of what this app used to
 * do. The old quote machinery walked the priced collection, measured pixels
 * against a scale calibration, applied a waste percentage and produced a
 * number - and every one of those steps was a place for the picture to be
 * wrong about the house.
 *
 * A contractor measures a roof with a wheel or a tape, standing in the
 * driveway. He knows the footage. Asking software to guess it from a
 * photograph, and then billing from the guess, is inventing a risk that did
 * not exist. So the numbers are TYPED, and this file multiplies.
 *
 * ONE SHAPE FOR EVERYTHING:  how many  x  each  x  price per
 *
 *   roofline   240 ft            x 1        x $2.50 /ft
 *   garland      3 pieces        x 1        x $150  each
 *   wreaths      2 wreaths       x 1        x $200  each
 *   palms        4 trees         x 6 strands x $35  /strand
 *
 * The middle number is the one that only palms and trees need, and it is why
 * a single shape covers the whole trade rather than four special cases. It
 * defaults to one and hides itself where it is always one.
 *
 * WHY A ROOFLINE IS ONE LINE. A house has six roof runs and the customer is
 * buying "the roofline". Nobody itemizes a fascia. The drawing has as many
 * runs as the roof has edges; the quote has one line, and the person types the
 * total footage they measured. A second line is available for the rare job
 * that really is two prices, and that is a button rather than the default.
 *
 * Money is integer cents internally, because 0.1 + 0.2 is not 0.3 in binary
 * floating point and a quote that is off by a cent is a quote somebody has to
 * explain.
 */

import type { LightCategory } from '../store/useEditor'

export interface QuoteLine {
  /** Feet, pieces, wreaths, trees. Whatever this kind of work is counted in. */
  count: number
  /** Strands per tree, and one for everything else. */
  each: number
  /** Dollars per foot, per piece, per strand. */
  price: number
  /** Overrides the category's own name, for a second line or an extra. */
  label?: string
  /**
   * What it is lit with, in the customer's words: "Warm White", "Red and
   * White". Typed, not read off the drawing, because a quote goes out before
   * anybody has drawn anything half the time.
   */
  lighting: string
  /** Anything the customer should see about this line. */
  note: string
}

/**
 * A quote is an ordered list, not a map, because a person can want two
 * roofline lines and the order they typed them in is the order they read.
 */
export interface QuoteItem extends QuoteLine {
  id: string
  /** What kind of work. Extras that match no category use 'other'. */
  category: LightCategory
}

export type QuoteLines = QuoteItem[]

/** What each kind of work is counted in, in the person's own words. */
export interface CountWords {
  /** "Linear feet", "How many" */
  count: string
  /** "Per foot", "Price each" */
  price: string
  /** Set only where the middle number is real. */
  each?: string
}

export const WORDS: Record<LightCategory, CountWords> = {
  roofline: { count: 'Linear feet', price: 'Per foot' },
  garland: { count: 'How many', price: 'Per piece' },
  palms: { count: 'How many trees', each: 'Strands each', price: 'Per strand' },
  trees: { count: 'How many trees', each: 'Strands each', price: 'Per strand' },
  clusters: { count: 'How many', price: 'Price each' },
  columns: { count: 'How many', each: 'Strands each', price: 'Per strand' },
  wreaths: { count: 'How many', price: 'Price each' },
  accessories: { count: 'How many', price: 'Price each' },
  other: { count: 'How many', price: 'Price each' },
}

/**
 * The prices offered in the dropdown, per kind of work.
 *
 * Starting points, not a rate card: every one of them is also typeable, and
 * whatever gets typed joins the list for the rest of the quote. A contractor
 * should never have to open a settings screen before he can write a price.
 */
export const PRICE_PRESETS: Record<LightCategory, number[]> = {
  /*
   * Roofline runs from two dollars a foot to twelve, and the spread is real
   * rather than sloppy: at the bottom it is bulbs on a single-storey fascia
   * somebody else takes down, and at the top it is a two-storey with steep
   * pitch, all-in, including the removal in January and storing the strands
   * until next year. A list that stopped at five told the top half of this
   * trade the app was not built for them.
   */
  roofline: [2, 3, 4, 5, 6, 8, 10, 12],
  garland: [100, 150, 200, 250, 300, 400],
  palms: [30, 35, 40, 45, 50, 60],
  trees: [30, 35, 40, 45, 50, 60],
  clusters: [50, 75, 100, 150, 200],
  columns: [30, 35, 40, 45, 50, 60],
  wreaths: [100, 150, 200, 250, 300, 400, 500, 600],
  accessories: [25, 50, 75, 100, 150, 200],
  other: [50, 100, 150, 200, 300],
}

/**
 * THE LINES THAT ARE NOT DRAWN.
 *
 * Every category above is something somebody traces onto a photograph. These
 * are the rest of the invoice: the work, the hardware and the visits that a
 * customer is genuinely charged for and that no drawing tool will ever produce
 * a shape for.
 *
 * They are ordinary lines with a name already filled in rather than a new kind
 * of thing, because the arithmetic is identical - how many, times each, times
 * a price - and inventing a second machinery for a takedown fee would be a
 * second machinery to keep correct.
 *
 * TAKEDOWN IS FIRST BECAUSE IT IS THE ONE THAT GETS FORGOTTEN. A season's
 * profit lives in January, and a quote written without it is a quote that has
 * priced eleven months of a twelve month job.
 */
export interface ExtraLine {
  label: string
  /** What it is usually counted in, when that is not obvious. */
  hint?: string
}

export const EXTRA_LINES: ExtraLine[] = [
  { label: 'Takedown and removal', hint: 'January, and the reason a season is profitable' },
  { label: 'Storage', hint: 'Holding the strands until next year' },
  { label: 'Timers', hint: 'Per timer' },
  { label: 'Cords and stakes', hint: 'Extension cords, stakes, clips' },
  { label: 'Mini trees', hint: 'Per tree' },
  { label: 'Bows', hint: 'Per bow' },
  { label: 'Trip charge', hint: 'Distance, or a second visit to finish' },
  { label: 'Service call', hint: 'A visit inside the season that is not covered' },
]

export const emptyLine = (): QuoteLine => ({
  count: 0,
  each: 1,
  price: 0,
  lighting: 'Warm White',
  note: '',
})

const cents = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 100)
const num = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0)

/**
 * What one line comes to, in dollars.
 *
 * Rounded to the cent once, at the end of the multiplication, so the lines and
 * the total cannot disagree by a rounding step. A quote whose parts do not add
 * up to its own total is worse than no quote.
 */
export function lineTotal(l: QuoteLine): number {
  return Math.round(num(l.count) * num(l.each) * cents(l.price)) / 100
}

/** The sum of the lines, and never anything else. */
export function quoteTotal(lines: QuoteLines): number {
  let c = 0
  for (const l of lines) c += Math.round(lineTotal(l) * 100)
  return c / 100
}

/*
 * ONE MONEY FORMAT IN THE PRODUCT, AND IT IS THE PAPER'S.
 *
 * There were two. This one always printed two decimals, so the home screen
 * read $14,755.00 while the quote beside it, the invoice, the job page and
 * the printed sheet all read $14,755. Three characters of nothing on every
 * amount on the one screen that has the least room for them: two figure
 * cards fit across a phone, and those three characters are what pushed a
 * seven digit number past the edge of its card.
 *
 * Re-exported rather than deleted so nothing that reaches for money here
 * silently gets a different answer than the sheet the homeowner is holding.
 */
export { money } from './paper'

